import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { Line } from 'react-chartjs-2';

import { fetchApiLogsLive, LIVE_REFRESH_MS, formatInTz } from '../helpers/apiLogs';
import { formatNumber, formatMs } from '../helpers/format';

/**
* "Now" card of the "API analytics (all requests)" page: the load of the
* last minute from `/data/api-logs/live` (straight from the logs, so it is
* live, unlike the summaries behind the other tabs). Refreshed every 15 s,
* paused while the browser tab is in the background.
*
* Compared with the period's average req/s from the overview: over 2× is
* flagged warning, over 4× critical - with an icon and a label, never color
* alone.
*/

const SPARK_MINUTES = 6;

const LEVELS = {
    warning: { background: '#FFF8E6', border: '#fab219', color: '#7a5200', icon: '▲' },
    critical: { background: '#FDEEEE', border: '#d03b3b', color: '#a12b2b', icon: '●' },
};

const styles = {
    card: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20,
        backgroundColor: '#FFFFFF', borderRadius: 6, padding: '14px 18px', marginBottom: 14,
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', borderLeft: '4px solid transparent',
    },
    label: { fontSize: 11, fontWeight: 600, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 },
    value: { fontSize: 28, fontWeight: 600, color: '#3A3F56', lineHeight: 1.1 },
    unit: { fontSize: 13, color: '#818FA5', fontWeight: 400 },
    stats: { fontSize: 12, color: '#52514E', display: 'flex', flexWrap: 'wrap', gap: '4px 14px' },
    muted: { fontSize: 11, color: '#818FA5' },
};

const isNum = (v) => v !== null && v !== undefined && !isNaN(Number(v));

/** the last few minutes, with 0 for minutes that had no requests (the backend leaves them out) */
const sparkPoints = (data) => {
    let end = moment(data.at || undefined).startOf('minute');
    let byMinute = {};
    (data.minutes || []).forEach((m) => { byMinute[moment(m.at).startOf('minute').valueOf()] = m.requests || 0; });

    let points = [];
    for (let i = SPARK_MINUTES - 1; i >= 0; i--) {
        let at = moment(end).subtract(i, 'minutes');
        points.push({ at, requests: byMinute[at.valueOf()] || 0 });
    }
    return points;
};

/**
* Why a replica looks wrong in the last minute (null = fine): no requests
* while the others get some, more than 20% errors, or a lagging event loop.
*/
const replicaProblem = (r, anyTraffic, lang) => {
    if (anyTraffic && !(Number(r.requests) > 0)) return 'no requests in the last minute'.translate(lang);
    if (isNum(r.errorPercent) && Number(r.errorPercent) > 20) return `${Number(r.errorPercent).toFixed(1)}% ${'errors'.translate(lang)}`;
    if (isNum(r.maxEventLoopLagMs) && Number(r.maxEventLoopLagMs) > 500) return `${'event loop lag'.translate(lang)} ${formatMs(r.maxEventLoopLagMs)}`;
    return null;
};

/** "R1 4.1 req/s · R2 3.9 · R3 4.3", a replica with a problem in red with a ● */
function ReplicaRow({ byReplica, lang }) {
    let rows = (byReplica || []).filter((r) => isNum(r.replica)).sort((a, b) => Number(a.replica) - Number(b.replica));
    if (!rows.length) return null;
    let anyTraffic = rows.some((r) => Number(r.requests) > 0);

    return (
        <div style={{ fontSize: 12, color: '#52514E', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '2px 0' }}>
            {rows.map((r, idx) => {
                let problem = replicaProblem(r, anyTraffic, lang);
                let rate = isNum(r.perSecond) ? Number(r.perSecond).toFixed(1) : '—';
                return (
                    <span
                        key={r.replica}
                        style={{ color: problem ? '#d03b3b' : undefined, fontWeight: problem ? 600 : 400, whiteSpace: 'nowrap' }}
                        title={`${'Replica'.translate(lang)} ${r.replica}: ${formatNumber(r.requests)} ${'req in the last minute'.translate(lang)}${isNum(r.avgMs) ? `, ${'avg'.translate(lang)} ${formatMs(r.avgMs)}` : ''}${problem ? ` - ${problem}` : ''}`}
                    >
                        {idx > 0 ? <span style={{ color: '#818FA5', fontWeight: 400 }}>{' · '}</span> : null}
                        {problem ? '● ' : ''}R{r.replica} {rate}{idx === 0 ? ' req/s' : ''}
                    </span>
                );
            })}
        </div>
    );
}

function Sparkline({ points, lang }) {
    return (
        <div style={{ width: 180, height: 46 }}>
            <Line
                data={{
                    labels: points.map((p) => p.at.format('HH:mm')),
                    datasets: [{
                        data: points.map((p) => p.requests),
                        borderColor: '#2a78d6',
                        backgroundColor: 'rgba(42, 120, 214, 0.12)',
                        fill: true,
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 3,
                        tension: 0.3,
                    }],
                }}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: false,
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                        legend: { display: false },
                        tooltip: { displayColors: false, callbacks: { label: (ctx) => `${formatNumber(ctx.parsed.y)} ${'req/min'.translate(lang)}` } },
                    },
                    scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
                }}
            />
        </div>
    );
}

/**
* @param {number} [avgPerSecond] - the period's average req/s (`totals.rates.avgPerSecond`), to flag unusual load
*/
function ApiLogsLive({ avgPerSecond, lang }) {
    let [data, setData] = useState(null);
    let [error, setError] = useState(null);
    let timer = useRef(null);
    let alive = useRef(true);

    useEffect(() => {
        alive.current = true;

        let load = () => {
            fetchApiLogsLive().then(({ data: result, error: err }) => {
                if (!alive.current) return;
                setError(err);
                if (!err) setData(result);
            });
        };

        let start = () => {
            if (timer.current) return;
            load();
            timer.current = setInterval(load, LIVE_REFRESH_MS);
        };

        let stop = () => {
            if (timer.current) clearInterval(timer.current);
            timer.current = null;
        };

        // only poll while the page is actually visible
        let onVisibility = () => (document.hidden ? stop() : start());

        if (!document.hidden) start();
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            alive.current = false;
            stop();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    if (!data) {
        return error ? (
            <div style={{ ...styles.card, ...styles.muted }}>{`${'Now'.translate(lang)}: ${error}`}</div>
        ) : null;
    }

    let last = data.lastMinute || {};
    let perSecond = Number(last.perSecond) || 0;
    let ratio = isNum(avgPerSecond) && Number(avgPerSecond) > 0 ? perSecond / Number(avgPerSecond) : null;
    let level = ratio === null ? null : ratio > 4 ? 'critical' : ratio > 2 ? 'warning' : null;
    let lv = level ? LEVELS[level] : null;
    let peak = data.peakSecond;

    return (
        <div style={{ ...styles.card, ...(lv ? { backgroundColor: lv.background, borderLeftColor: lv.border } : null) }}>
            <div>
                <div style={styles.label}>
                    <span className="live-dot" aria-hidden="true" />
                    {'Now'.translate(lang)}
                </div>
                <div style={styles.value}>
                    {perSecond.toFixed(2)} <span style={styles.unit}>req/s</span>
                </div>
                <ReplicaRow byReplica={data.byReplica} lang={lang} />
            </div>

            <div style={{ flex: '1 1 260px' }}>
                <div style={styles.stats}>
                    <span>{`${formatNumber(last.requests)} ${'req in the last minute'.translate(lang)}`}</span>
                    <span>{`${'errors'.translate(lang)} ${isNum(last.errorPercent) ? `${Number(last.errorPercent).toFixed(1)}%` : '—'}`}</span>
                    <span>{`${'avg'.translate(lang)} ${isNum(last.avgMs) ? formatMs(last.avgMs) : '—'}`}</span>
                    <span>
                        {`${'peak'.translate(lang)} ${peak && isNum(peak.requests) ? `${formatNumber(peak.requests)} req/s` : '—'}`}
                        {peak && peak.at ? <span style={styles.muted}>{` (${formatInTz(peak.at, { seconds: true, date: false })})`}</span> : null}
                    </span>
                </div>
                {lv ? (
                    <div style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: lv.color }}>
                        {`${lv.icon} ${'Unusually high load'.translate(lang)} - ${ratio.toFixed(1)}× ${'the period average'.translate(lang)} (${Number(avgPerSecond).toFixed(2)} req/s)`}
                    </div>
                ) : null}
                <div style={{ ...styles.muted, marginTop: 4 }}>
                    {`${'Live, refreshed every 15 s'.translate(lang)} · ${formatInTz(data.at, { seconds: true, date: false })}`}
                    {error ? ` · ${'last refresh failed'.translate(lang)}` : ''}
                </div>
            </div>

            <Sparkline points={sparkPoints(data)} lang={lang} />
        </div>
    );
}

export default ApiLogsLive;
